import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Star } from "lucide-react";
import { Logo } from "../components/Logo";
import { api, errMsg } from "../lib/api";

const input = "mt-1.5 w-full border border-white/15 bg-black/40 px-3 text-sm text-white focus:border-[#a259ff] focus:outline-none";
const lbl = "block font-mono text-[11px] uppercase tracking-widest text-slate-400";

const Stars = ({ value, onChange }) => (
  <div className="mt-2 flex gap-1" role="radiogroup" aria-label="Rating">
    {[1, 2, 3, 4, 5].map((n) => (
      <button type="button" key={n} onClick={() => onChange(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`} data-testid={`review-star-${n}`} className="p-1 transition-transform duration-200 hover:scale-110">
        <Star className={`h-8 w-8 ${n <= value ? "fill-[#e9d5ff] text-[#e9d5ff]" : "text-slate-500"}`} />
      </button>
    ))}
  </div>
);

export default function ReviewPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const k = params.get("k") ?? "";
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ name: "", text: "", rating: 5 });
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/orders/${id}/review`, { params: { k } }).then((r) => setInfo(r.data ?? false)).catch(() => setInfo(false));
  }, [id, k]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/orders/${id}/review`, f, { params: { k } });
      setSent(r.data?.published ? "published" : "pending");
    } catch (err) {
      setSent({ error: errMsg(err) });
    }
    setBusy(false);
  };

  const box = "mx-auto mt-14 max-w-lg border border-white/10 bg-[#0b0b11]/90 p-7 md:p-10";
  return (
    <div className="grain grid-lines min-h-screen bg-[#07070b] px-5 py-8 md:px-10">
      <Link to="/" data-testid="review-home-link"><Logo /></Link>
      <div className={box} data-testid="review-page">
        <h1 className="font-display text-5xl uppercase leading-none text-white">Rate your order</h1>
        {info === null && <p className="mt-6 text-slate-400">Loading…</p>}
        {info === false && <p className="mt-6 text-slate-300" data-testid="review-not-found">We couldn't open that order. Use the link from your email.</p>}
        {info && sent === "published" && <p className="mt-6 text-slate-200" data-testid="review-thanks">Thanks! Your review is now live on the site.</p>}
        {info && sent === "pending" && <p className="mt-6 text-slate-200" data-testid="review-thanks">Thanks! Your review will appear on the site once it's checked.</p>}
        {info && !sent && info.already_reviewed && <p className="mt-6 text-slate-300" data-testid="review-already">You've already reviewed this order. Thank you!</p>}
        {info && !sent && !info.already_reviewed && !info.delivered && <p className="mt-6 text-slate-300" data-testid="review-not-delivered">You can leave a review once your account has been delivered.</p>}
        {info && (!sent || sent.error) && !info.already_reviewed && info.delivered && (
          <form onSubmit={submit} className="mt-6 space-y-5" data-testid="review-form">
            <p className="text-slate-300">How was <b className="text-white">{info.product_name}</b>?</p>
            <div className={lbl}>Your rating<Stars value={f.rating} onChange={(rating) => setF({ ...f, rating })} /></div>
            <label className={lbl}>Name to show<input required maxLength={60} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Jay R." className={`${input} h-11`} data-testid="review-form-name-input" /></label>
            <label className={lbl}>Your review<textarea required minLength={3} maxLength={600} rows={4} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className={`${input} py-2`} data-testid="review-form-text-input" /></label>
            {sent?.error && <p className="text-sm text-[#ff7ad9]" data-testid="review-form-error">{sent.error}</p>}
            <button disabled={busy} className="h-12 w-full bg-[#ff3dc8] font-mono text-xs font-bold uppercase tracking-widest text-white disabled:opacity-60" data-testid="review-form-submit">{busy ? "Sending…" : "Post review"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
